import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Adresse de livraison sélectionnée (style Uber Eats).
 * `selected` = null → "Conakry" par défaut (tous les restaurants).
 * `selected` = { label, zone, address_id? } → filtre la liste par zone.
 * Persistée dans localStorage pour survivre aux rechargements.
 */
const AddressContext = createContext(null);
const LS_KEY = "neldaari_delivery_address";

export function AddressProvider({ children }) {
  const { user, loading } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || null; } catch { return null; }
  });

  const refresh = useCallback(async () => {
    if (!user || user.role !== "client") { setAddresses([]); return; }
    try {
      const { data } = await apiClient.get("/me/addresses");
      setAddresses(data || []);
    } catch { setAddresses([]); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const select = useCallback((addr) => {
    setSelected(addr);
    try {
      if (addr) localStorage.setItem(LS_KEY, JSON.stringify(addr));
      else localStorage.removeItem(LS_KEY);
    } catch { /* stockage indisponible */ }
  }, []);

  // Déconnexion (auth résolue) → on oublie l'adresse enregistrée du compte.
  // Ne rien faire tant que l'auth charge, sinon on efface la sélection au boot.
  useEffect(() => {
    if (!loading && !user && selected?.address_id) select(null);
  }, [user, loading, selected, select]);

  return (
    <AddressContext.Provider value={{ addresses, selected, select, refresh }}>
      {children}
    </AddressContext.Provider>
  );
}

export const useDeliveryAddress = () => useContext(AddressContext);
